/**
 * Xero Integration Configuration
 * Handles Australian accounting requirements and GST compliance
 */

export interface XeroConfig {
  clientId: string;
  clientSecret: string;
  redirectUrl: string;
  scopes: string[];
  state: string;
}

export interface XeroAccountMapping {
  // Standard chart of accounts for Australian motor dealers
  salesRevenue: string;        // 4-0000 Sales Revenue
  gstOnSales: string;         // 2-0210 GST on Sales  
  lctPayable: string;         // 2-0220 LCT Payable
  stampDutyPayable: string;   // 2-0230 Stamp Duty Payable
  accountsReceivable: string; // 1-0200 Accounts Receivable
  inventory: string;          // 1-0100 Vehicle Inventory
  costOfGoodsSold: string;    // 5-0000 Cost of Goods Sold
  tradeIns: string;          // 1-0110 Trade-in Vehicles
  financingRevenue: string;   // 4-0100 Financing Commission
  depositLiability: string;   // 2-0100 Customer Deposits
}

export const DEFAULT_XERO_CONFIG: XeroConfig = {
  clientId: import.meta.env.VITE_XERO_CLIENT_ID || '',
  clientSecret: import.meta.env.VITE_XERO_CLIENT_SECRET || '',
  redirectUrl: `${typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5174'}/xero/callback`,
  scopes: [
    'accounting.transactions',
    'accounting.contacts', 
    'accounting.settings',
    'offline_access'
  ],
  state: 'dealership-management-system'
};

export const DEFAULT_ACCOUNT_MAPPING: XeroAccountMapping = {
  salesRevenue: '200',        // Sales - Revenue account
  gstOnSales: '820',          // Sales Tax - Current Liability
  lctPayable: '800',          // Accounts Payable - Current Liability (or create custom)
  stampDutyPayable: '260',    // Other Revenue - Revenue account (or create custom liability)
  accountsReceivable: '610',  // Accounts Receivable - Current Asset
  inventory: '630',           // Inventory - Inventory Asset
  costOfGoodsSold: '310',     // Cost of Goods Sold - Direct Costs
  tradeIns: '630',            // Inventory - Inventory Asset (use same as inventory)
  financingRevenue: '260',    // Other Revenue - Revenue account
  depositLiability: '800'     // Accounts Payable - Current Liability
};

export const XERO_ENDPOINTS = {
  auth: 'https://login.xero.com/identity/connect/authorize',
  token: 'https://identity.xero.com/connect/token',
  api: 'https://api.xero.com/api.xro/2.0', // Direct Xero API (for server-side use only)
  backend: import.meta.env.VITE_XERO_PROXY_URL || 'https://xero-dealeros.up.railway.app/api/xero'
} as const;

// Australian GST Tax Types in Xero API
// Reference: https://developer.xero.com/documentation/api/accounting/types
export const XERO_TAX_TYPES = {
  gstOnIncome: 'OUTPUT',          // GST on Income (10%) - for sales revenue
  gstFreeIncome: 'EXEMPTOUTPUT',  // GST Free Income - for tax-free sales
  inputTaxed: 'INPUT',            // GST on Purchases (10%) - for expenses
  gstFreeExpenses: 'EXEMPTEXPENSES', // GST Free Expenses - for tax-free purchases
  basExcluded: 'BASEXCLUDED',     // BAS Excluded - not reported on BAS
  none: 'NONE'                    // No tax
} as const;

export type XeroTaxType = typeof XERO_TAX_TYPES[keyof typeof XERO_TAX_TYPES];

/**
 * Common Australian motor-dealer GL accounts (Xero default chart).
 * Used as the default dropdown for supplier "Default Xero Account" and per-line GL coding.
 *
 * After Xero OAuth is connected, the live account list from the org's Xero
 * is merged in. This is the offline/initial-setup default.
 */
export interface XeroAccountOption {
  code: string;
  name: string;
  category: 'Revenue' | 'Direct Cost' | 'Operating Expense' | 'Asset' | 'Liability';
}

export const XERO_DEALER_ACCOUNTS: XeroAccountOption[] = [
  // Revenue
  { code: '200', name: 'Sales',                              category: 'Revenue' },
  { code: '260', name: 'Other Revenue',                      category: 'Revenue' },
  { code: '270', name: 'Interest Income',                    category: 'Revenue' },

  // Direct costs (vehicle COGS)
  { code: '310', name: 'Cost of Goods Sold',                 category: 'Direct Cost' },
  { code: '311', name: 'Vehicle Reconditioning',             category: 'Direct Cost' },
  { code: '312', name: 'Vehicle Detailing',                  category: 'Direct Cost' },
  { code: '313', name: 'Vehicle Freight & Transport',        category: 'Direct Cost' },
  { code: '314', name: 'Mechanical Repairs - Stock',         category: 'Direct Cost' },
  { code: '315', name: 'Body Repairs - Stock',               category: 'Direct Cost' },
  { code: '316', name: 'Tyres & Wheels - Stock',             category: 'Direct Cost' },
  { code: '317', name: 'Parts & Accessories',                category: 'Direct Cost' },
  { code: '318', name: 'Roadworthy / Inspection',            category: 'Direct Cost' },
  { code: '319', name: 'Registration & Transfer Fees',       category: 'Direct Cost' },

  // Operating expenses
  { code: '400', name: 'Advertising & Marketing',            category: 'Operating Expense' },
  { code: '404', name: 'Bank Fees',                          category: 'Operating Expense' },
  { code: '408', name: 'Cleaning',                           category: 'Operating Expense' },
  { code: '412', name: 'Consulting & Accounting',            category: 'Operating Expense' },
  { code: '416', name: 'Depreciation',                       category: 'Operating Expense' },
  { code: '420', name: 'Entertainment',                      category: 'Operating Expense' },
  { code: '424', name: 'Freight & Courier (non-stock)',      category: 'Operating Expense' },
  { code: '429', name: 'General Expenses',                   category: 'Operating Expense' },
  { code: '433', name: 'Insurance',                          category: 'Operating Expense' },
  { code: '437', name: 'Interest Expense',                   category: 'Operating Expense' },
  { code: '441', name: 'Legal Expenses',                     category: 'Operating Expense' },
  { code: '445', name: 'Light, Power, Heating',              category: 'Operating Expense' },
  { code: '449', name: 'Motor Vehicle Expenses',             category: 'Operating Expense' },
  { code: '453', name: 'Office Expenses',                    category: 'Operating Expense' },
  { code: '461', name: 'Printing & Stationery',              category: 'Operating Expense' },
  { code: '469', name: 'Rent',                               category: 'Operating Expense' },
  { code: '473', name: 'Repairs & Maintenance (premises)',   category: 'Operating Expense' },
  { code: '477', name: 'Wages & Salaries',                   category: 'Operating Expense' },
  { code: '478', name: 'Superannuation',                     category: 'Operating Expense' },
  { code: '485', name: 'Subscriptions',                      category: 'Operating Expense' },
  { code: '489', name: 'Telephone & Internet',               category: 'Operating Expense' },
  { code: '493', name: 'Travel - National',                  category: 'Operating Expense' },

  // Assets / Liabilities (rare for supplier default — included for completeness)
  { code: '610', name: 'Accounts Receivable',                category: 'Asset' },
  { code: '630', name: 'Inventory',                          category: 'Asset' },
  { code: '800', name: 'Accounts Payable',                   category: 'Liability' },
  { code: '820', name: 'GST',                                category: 'Liability' },
];

/** Tax types presented as a dropdown — labels are user-friendly. */
export const XERO_TAX_TYPE_OPTIONS: { value: XeroTaxType; label: string }[] = [
  { value: 'INPUT',           label: 'GST on Purchases (10%)' },
  { value: 'EXEMPTEXPENSES',  label: 'GST Free Expenses' },
  { value: 'OUTPUT',          label: 'GST on Income (10%)' },
  { value: 'EXEMPTOUTPUT',    label: 'GST Free Income' },
  { value: 'BASEXCLUDED',     label: 'BAS Excluded' },
  { value: 'NONE',            label: 'No Tax' },
];
